import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import NotificationsOffIcon from "@material-ui/icons/NotificationsOff";
import PhoneIphoneIcon from "@material-ui/icons/PhoneIphone";
import {
  activatePushDevice,
  getPushDiagnostics,
  sendPushTestToThisDevice,
  unsubscribeFromPush
} from "../../services/pushNotification";

const formatDate = value => {
  if (!value) return "Nunca";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "medium"
    }).format(new Date(value));
  } catch (_err) {
    return String(value);
  }
};

const errorMessages = {
  HTTPS_REQUIRED: "O Estratiza Hub precisa estar aberto em HTTPS.",
  NOT_SUPPORTED: "Este navegador não oferece Service Worker e Web Push.",
  IOS_VERSION_REQUIRED: "Atualize o iPhone/iPad para iOS/iPadOS 16.4 ou superior.",
  IOS_INSTALL_REQUIRED: "No iPhone/iPad, instale e abra o Estratiza Hub pela Tela de Início.",
  PERMISSION_DENIED: "A permissão foi bloqueada. Libere as notificações nos ajustes do navegador/aparelho.",
  PERMISSION_PENDING: "A permissão ainda não foi concedida.",
  VAPID_UNAVAILABLE: "A chave de notificação do servidor não está disponível.",
  SERVICE_WORKER_FAILED: "O Service Worker não ficou ativo.",
  SUBSCRIBE_FAILED: "Não foi possível registrar este aparelho.",
  NOT_SUBSCRIBED: "Ative as notificações neste aparelho antes de testar.",
  TEST_FAILED: "O provedor não aceitou a notificação de teste."
};

const DiagnosticRow = ({ ok, label, value }) => (
  <ListItem dense>
    <ListItemIcon style={{ minWidth: 34 }}>
      {ok ? (
        <CheckCircleIcon style={{ color: "#2e7d32", fontSize: 20 }} />
      ) : (
        <ErrorIcon style={{ color: "#c62828", fontSize: 20 }} />
      )}
    </ListItemIcon>
    <ListItemText primary={label} secondary={value} />
  </ListItem>
);

const PushDeviceManager = ({ mobileNav = false }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      setDiagnostics(await getPushDiagnostics());
    } catch (err) {
      setFeedback(err.message || "Falha no diagnóstico");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleOpen = () => {
    setOpen(true);
    setFeedback("");
    refresh();
  };

  const handleActivate = async () => {
    setLoading(true);
    setFeedback("");
    const result = await activatePushDevice();
    if (result.ok) {
      setFeedback("Notificações ativadas neste aparelho.");
      setDiagnostics(result.diagnostics);
    } else {
      setFeedback(errorMessages[result.code] || result.message || "Falha ao ativar.");
      await refresh();
    }
    setLoading(false);
  };

  const handleDisable = async () => {
    setLoading(true);
    const ok = await unsubscribeFromPush();
    setFeedback(
      ok
        ? "Notificações desativadas somente neste aparelho."
        : "Não foi possível desativar este aparelho."
    );
    await refresh();
    setLoading(false);
  };

  const handleTest = async () => {
    setLoading(true);
    setFeedback("Enviando teste...");
    const result = await sendPushTestToThisDevice();
    if (!result.ok) {
      setFeedback(errorMessages[result.code] || result.message || "Falha no teste.");
      setLoading(false);
      return;
    }
    setFeedback("Provedor aceitou. Aguarde a notificação aparecer com o app em segundo plano.");
    window.setTimeout(() => refresh({ silent: true }), 2500);
    window.setTimeout(() => refresh({ silent: true }), 6000);
    setLoading(false);
  };

  const active = Boolean(
    diagnostics?.localSubscription &&
    diagnostics?.backendConfirmed &&
    !diagnostics?.disabledAt
  );
  const iosNeedsInstall =
    diagnostics?.platform === "ios" && !diagnostics?.standalone;
  const iosNeedsUpdate =
    diagnostics?.platform === "ios" && !diagnostics?.iosVersionSupported;
  const deviceLabel =
    diagnostics?.platform === "ios"
      ? "iPhone"
      : diagnostics?.platform === "android"
      ? "Android"
      : "aparelho";
  const canActivate =
    diagnostics?.https &&
    diagnostics?.serviceWorkerSupported &&
    diagnostics?.pushManagerSupported &&
    !iosNeedsInstall &&
    !iosNeedsUpdate &&
    diagnostics?.permission !== "denied";

  return (
    <>
      {mobileNav ? (
        <button
          type="button"
          onClick={handleOpen}
          aria-label="Notificações deste aparelho"
          style={{
            width: "100%",
            minHeight: 60,
            padding: "10px 4px",
            border: "none",
            background: "none",
            color: active ? "#25d366" : "rgba(255,255,255,0.72)",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            WebkitTapHighlightColor: "transparent"
          }}
        >
          {active ? (
            <NotificationsActiveIcon style={{ fontSize: 24 }} />
          ) : (
            <NotificationsOffIcon style={{ fontSize: 24 }} />
          )}
          <span
            style={{
              fontSize: 10,
              fontWeight: active ? 700 : 400,
              letterSpacing: 0.3,
              lineHeight: 1
            }}
          >
            Notificações
          </span>
        </button>
      ) : (
        <Button
          fullWidth
          size="small"
          variant={active ? "outlined" : "contained"}
          color="primary"
          startIcon={active ? <NotificationsActiveIcon /> : <NotificationsOffIcon />}
          onClick={handleOpen}
          style={{ textTransform: "none", justifyContent: "flex-start" }}
        >
          {active
            ? "Notificações ativas neste aparelho"
            : "Ativar notificações neste aparelho"}
        </Button>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Notificações deste aparelho</DialogTitle>
        <DialogContent dividers>
          <Box
            p={2}
            mb={2}
            borderRadius={8}
            bgcolor={active ? "#e8f5e9" : "#f4f7fb"}
            border={`1px solid ${active ? "#a5d6a7" : "#d7dee8"}`}
          >
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
              {active
                ? `Notificações ativas neste ${deviceLabel}`
                : `Ative as notificações neste ${deviceLabel}`}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              {active
                ? "Este aparelho já está registrado. Use o teste para confirmar o recebimento."
                : `Toque no botão abaixo e depois escolha Permitir no aviso do ${deviceLabel}.`}
            </Typography>
            <Button
              fullWidth
              color="primary"
              variant="contained"
              disabled={loading || (!active && !canActivate)}
              onClick={active ? handleTest : handleActivate}
              style={{ marginTop: 14, textTransform: "none", fontWeight: 700 }}
            >
              {loading
                ? "Aguarde..."
                : active
                ? "Enviar notificação de teste"
                : "Ativar agora"}
            </Button>
          </Box>

          {diagnostics?.platform === "ios" && (iosNeedsInstall || iosNeedsUpdate) && (
            <Box
              mt={2}
              mb={2}
              p={2}
              border="1px solid #d7dee8"
              borderRadius={8}
              bgcolor="#f7f9fc"
            >
              <Box display="flex" alignItems="center" mb={1}>
                <PhoneIphoneIcon style={{ marginRight: 8 }} />
                <Typography variant="subtitle2">iPhone/iPad</Typography>
              </Box>
              <Typography variant="body2">
                {iosNeedsUpdate
                  ? "Atualize o iPhone/iPad para iOS/iPadOS 16.4 ou superior."
                  : "Abra o Estratiza Hub pelo ícone instalado na Tela de Início para ativar."}
              </Typography>
            </Box>
          )}

          {diagnostics?.platform === "android" && (
            <Box mt={2} mb={2} p={2} border="1px solid #d7dee8" borderRadius={8}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                O Estratiza Hub já envia o aviso como alerta de alta prioridade.
              </Typography>
              <Typography variant="body2" style={{ marginTop: 6 }}>
                Se aparecer apenas o ícone na barra superior, o Android deixou
                este canal como silencioso ou sem pop-up. Abra Ajustes do
                aparelho → Notificações → Estratiza Hub (ou Chrome) → categorias de
                notificação → app.estratiza.com.br. Marque "Alerta" e
                ative "Mostrar como pop-up", "Aparecer na tela" ou
                "Notificação flutuante", conforme o fabricante.
              </Typography>
              <Typography variant="body2" style={{ marginTop: 6 }}>
                Essa permissão pertence ao sistema Android e não pode ser
                ligada automaticamente por um site. Instalar o Estratiza Hub pela
                tela inicial ajuda a identificar o canal. Só retire a economia
                extrema de bateria se os avisos também estiverem atrasando.
              </Typography>
            </Box>
          )}

          <Button
            size="small"
            onClick={() => setShowDiagnostics(value => !value)}
            style={{ textTransform: "none", paddingLeft: 0 }}
          >
            {showDiagnostics ? "Ocultar detalhes técnicos" : "Ver detalhes técnicos"}
          </Button>

          {showDiagnostics && loading && !diagnostics ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={28} />
            </Box>
          ) : showDiagnostics ? (
            <List dense>
              <DiagnosticRow
                ok={Boolean(diagnostics?.https)}
                label="HTTPS"
                value={diagnostics?.https ? "Seguro" : "Obrigatório"}
              />
              <DiagnosticRow
                ok={Boolean(diagnostics?.serviceWorkerActive)}
                label="Service Worker"
                value={diagnostics?.serviceWorkerActive ? "Ativo" : "Inativo"}
              />
              <DiagnosticRow
                ok={Boolean(diagnostics?.pushManagerSupported)}
                label="PushManager"
                value={diagnostics?.pushManagerSupported ? "Compatível" : "Não suportado"}
              />
              <DiagnosticRow
                ok={diagnostics?.permission === "granted"}
                label="Permissão"
                value={diagnostics?.permission || "Indisponível"}
              />
              <DiagnosticRow
                ok={Boolean(diagnostics?.localSubscription)}
                label="Inscrição local"
                value={diagnostics?.localSubscription ? "Encontrada" : "Ausente"}
              />
              <DiagnosticRow
                ok={Boolean(diagnostics?.backendConfirmed)}
                label="Registro no servidor"
                value={diagnostics?.backendConfirmed ? "Confirmado" : "Ausente"}
              />
              <DiagnosticRow
                ok={Boolean(diagnostics?.lastTestDeliveredAt)}
                label="Último teste exibido"
                value={formatDate(diagnostics?.lastTestDeliveredAt)}
              />
            </List>
          ) : null}

          {showDiagnostics && (
            <>
              <Divider />
              <Box mt={2}>
                <Typography variant="caption" color="textSecondary">
                  {diagnostics?.deviceName || "Identificando aparelho"}
                  {diagnostics?.vapidFingerprint
                    ? ` • VAPID ${diagnostics.vapidFingerprint}`
                    : ""}
                </Typography>
              </Box>
            </>
          )}

          {diagnostics?.disabledReason && (
            <Box mt={1}>
              <Typography variant="body2" style={{ color: "#c62828" }}>
                A inscrição foi desativada pelo servidor: {diagnostics.disabledReason}.
                Reative este aparelho.
              </Typography>
            </Box>
          )}

          {feedback && (
            <Box mt={2} p={1.5} bgcolor="#f1f4f8" borderRadius={6}>
              <Typography variant="body2">{feedback}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ flexWrap: "wrap" }}>
          {active && (
            <Button onClick={handleDisable} disabled={loading}>
              Desativar neste aparelho
            </Button>
          )}
          <Button onClick={() => setOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PushDeviceManager;
